/* eslint-disable camelcase */
import type { ICreditCardValues } from "../types";
import { Logger } from "utils/logger";
import { translate } from "utils/translations/translate";

const PUBLIC_KEY = "e18809971284f06f3afe236dee143b7f";
const CREDIT_CARD_LENGTH = 16;

interface IValidationResults {
  cardHolderName: string | null;
  cardNumber: string | null;
  cvc: string | null;
  email: string | null;
  expMonth: string | null;
  expYear: string | null;
}

const validate = (cardData: ICreditCardValues): IValidationResults => {
  const validCreditCards = [
    {
      name: "visa",
      pattern: /^4/u,
      validLength: [CREDIT_CARD_LENGTH],
    },
    {
      name: "mastercard",
      pattern: /^5[1-5]/u,
      validLength: [CREDIT_CARD_LENGTH],
    },
  ];

  const validateEmail = (email: string): string | null => {
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/iu.test(email)) return null;

    return translate.t("validations.email");
  };

  const validateCVC = (value: string): string | null => {
    if (/^\d{3,4}$/u.exec(value)) return null;

    return translate.t("organization.billing.CVCInvalid");
  };

  const validateExpMonth = (value: string): string | null => {
    if (/^\d{2}$/u.exec(value)) return null;

    return translate.t("organization.billing.creditCardExpMonthInvalid");
  };

  const validateExpYear = (value: string): string | null => {
    if (/^\d{4}$/u.exec(value)) return null;

    return translate.t("organization.billing.cardNumberInvalid");
  };

  const validateCardNumber = (value: string): string | null => {
    const data = validCreditCards.find((card): boolean =>
      card.pattern.test(value),
    );

    if (undefined === data)
      return translate.t("organization.billing.cardNumberInvalid");

    return data.validLength.includes(value.length)
      ? null
      : translate.t("organization.billing.cardNumberInvalid");
  };

  const validateName = (value: string): string | null => {
    if (value.length > 0) return null;

    return translate.t("validations.required");
  };

  const validation = {
    cardHolderName: validateName(cardData.cardHolderName),
    cardNumber: validateCardNumber(cardData.cardNumber),
    cvc: validateCVC(cardData.cvc),
    email: validateEmail(cardData.email),
    expMonth: validateExpMonth(cardData.expMonth),
    expYear: validateExpYear(cardData.expYear),
  };

  return validation;
};

const createCardToken = async (
  cardData: ICreditCardValues,
): Promise<{ token: string | null; error: string | null }> => {
  const validation = validate(cardData);
  const errors = Object.values(validation).filter(
    (value): boolean => value !== null,
  );

  if (errors.length > 0) {
    return { error: errors.join(", "), token: null };
  }

  try {
    ePayco.setPublicKey(PUBLIC_KEY);
  } catch (error: unknown) {
    Logger.error("Failed to set ePayco public key: ", error);
  }

  const token: string | null = await new Promise<string | null>(
    (resolve): void => {
      ePayco.token.create(
        {
          card: {
            cvc: cardData.cvc,
            email: cardData.email,
            exp_month: cardData.expMonth,
            exp_year: cardData.expYear,
            name: cardData.cardHolderName,
            number: cardData.cardNumber,
          },
        },
        (
          ePaycoErrors: { data: { description: string } } | null,
          ePaycoToken: string | null,
        ): void => {
          if (ePaycoErrors === null && typeof ePaycoToken === "string") {
            resolve(ePaycoToken);
          } else {
            Logger.error("ePayco token creation error: ", ePaycoErrors);
            resolve(null);
          }
        },
      );
    },
  ).catch((error: unknown): string | null => {
    Logger.error("ePayco token creation error: ", error);

    return null;
  });

  if (token === null) {
    return { error: "Invalid card information", token: null };
  }

  return { error: null, token };
};

export { createCardToken };
